import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, Mail, Lock, User, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, body);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created — 500 coins added!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/40">
            <Radar className="w-6 h-6 text-cyan-neon" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-400 radar-dot" />
          </span>
          <span className="font-display font-black text-2xl tracking-tight">ADS<span className="text-cyan-neon">RUSH</span></span>
        </div>

        <div className="glass rounded-2xl p-7">
          <div className="flex gap-2 p-1 bg-[#07090E] rounded-lg mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`switch-${m}`}
                className={`flex-1 py-2 rounded-md text-sm font-semibold capitalize transition ${
                  mode === m ? "bg-cyan-400/15 text-cyan-neon" : "text-slate-400"
                }`}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={upd("name")}
                  data-testid="name-input"
                  className="pl-10 bg-[#07090E] border-slate-700 h-11"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                required
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={upd("email")}
                data-testid="email-input"
                className="pl-10 bg-[#07090E] border-slate-700 h-11"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                required
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={upd("password")}
                data-testid="password-input"
                className="pl-10 bg-[#07090E] border-slate-700 h-11"
              />
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-auth-button" className="w-full btn-neon h-11 text-base">
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              {!busy && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-slate-800" />
            <span className="text-xs text-slate-500">OR</span>
            <span className="flex-1 h-px bg-slate-800" />
          </div>

          <button
            onClick={google}
            data-testid="google-login-button"
            className="w-full h-11 rounded-md border border-slate-700 bg-[#07090E] hover:border-cyan-500/40 transition flex items-center justify-center gap-3 text-sm font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </button>
        </div>

        <p className="text-center text-xs text-slate-600 mt-5">
          By continuing you agree to our Terms. Demo payouts run in sandbox mode.
        </p>
      </motion.div>
    </div>
  );
}
